import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUser(response.data);
          // Check if stored flag exists
          const flag = localStorage.getItem("must_change_password");
          if (flag === "true") setMustChangePassword(true);
        } catch (error) {
          console.error("Auth init error:", error);
          localStorage.removeItem("token");
          localStorage.removeItem("must_change_password");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (employee_id, password) => {
    const response = await axios.post(`${API}/auth/login`, {
      employee_id,
      password,
    });
    const { token: newToken, user: userData, must_change_password } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    if (must_change_password) {
      localStorage.setItem("must_change_password", "true");
      setMustChangePassword(true);
    }
    return userData;
  };

  const changePassword = async (newPassword) => {
    await axios.post(
      `${API}/auth/change-password`,
      { new_password: newPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    localStorage.removeItem("must_change_password");
    setMustChangePassword(false);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("must_change_password");
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`,
  });

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        getAuthHeaders,
        changePassword,
        mustChangePassword,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
